import { type ReactNode, useMemo } from 'react'
import { isDefined, isEmpty, type CompanyData, type TeamData } from '@terros/common'
import { type UseFormReturnType } from '@mantine/form'
import { Select } from '@mantine/core'
import { getLevelName, useProfile, useTeams, type SelectItem, useTranslation } from '@front/model'
import { type AddTeamValues } from './types'

type ParentTeamSelectProps = {
  form: UseFormReturnType<AddTeamValues>
  allowParentSelect?: boolean
}

export const ParentTeamSelect = ({ form, allowParentSelect }: ParentTeamSelectProps): ReactNode => {
  const { t } = useTranslation()
  const { data: allTeams } = useTeams()
  const { company } = useProfile()
  const formLevel = form.values.level
  const teamOptions = useMemo(() => {
    if (isEmpty(allTeams)) return []
    return allTeams.filter((team) => team.level > formLevel)
  }, [formLevel, allTeams])

  const companyHighestLevel = company?.highestTeamLevel
  if (isDefined(companyHighestLevel) && companyHighestLevel === formLevel) return

  return (
    <Select
      withAsterisk
      data={teamOptions.sort(sortTeamByLevel).map(toTeamOption(company))}
      label={t('Parent team')}
      placeholder={t('Select parent team')}
      {...form.getInputProps('parentId')}
    />
  )
}

const toTeamOption =
  (company?: CompanyData) =>
  (team: Pick<TeamData, 'teamId' | 'name' | 'level'>): SelectItem => {
    if (!company) return { value: team.teamId, label: team.name }
    return {
      value: team.teamId,
      label: `${getLevelName(company, team.level)}: ${team.name}`,
    }
  }

const sortTeamByLevel = (a: TeamData, b: TeamData): number => {
  if (a.level === b.level) {
    return a.name.localeCompare(b.name)
  }
  return a.level - b.level
}
